import { supabase } from "./supabase";

/**
 * Lógica reutilizable de subida de imágenes al almacenamiento de Supabase.
 * Extraída de ImageUploader para poder reutilizarla también en el editor
 * enriquecido (RichTextEditor) al insertar imágenes en línea.
 *
 * Flujo: redimensiona la imagen en el navegador (canvas), la sube vía la
 * Edge Function `upload-image` y, si falla, cae al storage directo. Devuelve
 * la URL pública final.
 */

/** Traduce errores técnicos a mensajes en español para el usuario. */
export function classifyUploadError(msg: string): string {
	const lower = msg.toLowerCase();
	if (
		lower.includes("bucket") ||
		lower.includes("not found") ||
		lower.includes("does not exist") ||
		lower.includes("storage")
	) {
		return "El almacenamiento de imagenes no esta configurado en el servidor. Pide al soporte que verifique el bucket 'images'.";
	}
	if (
		lower.includes("row violates") ||
		lower.includes("rls") ||
		lower.includes("policy") ||
		lower.includes("permission") ||
		lower.includes("not authorized") ||
		lower.includes("unauthorized") ||
		lower.includes("jwt") ||
		lower.includes("auth")
	) {
		return "No tienes permiso para subir imagenes. Verifica que hayas iniciado sesion y que tu cuenta tenga acceso al almacenamiento.";
	}
	if (
		lower.includes("fetch") ||
		lower.includes("network") ||
		lower.includes("failed to fetch") ||
		lower.includes("internet")
	) {
		return "Error de conexion. Verifica tu internet e intenta de nuevo.";
	}
	if (lower.includes("timeout") || lower.includes("abort")) {
		return "La subida tardo demasiado. Intenta con una imagen mas pequena.";
	}
	if (lower.includes("cors") || lower.includes("blocked")) {
		return "Error de configuracion del servidor (CORS). Contacta al soporte.";
	}
	if (
		lower.includes("413") ||
		lower.includes("too large") ||
		lower.includes("payload")
	) {
		return "La imagen es demasiado grande. Intenta con una mas pequena (max 15 MB).";
	}
	if (lower.includes("500") || lower.includes("internal server error")) {
		return "Error interno del servidor. Intenta de nuevo en unos minutos.";
	}
	return msg;
}

async function uploadViaEdgeFunction(
	blob: Blob,
	fileName: string,
	folder: string,
): Promise<string> {
	const formData = new FormData();
	formData.append("file", blob, fileName);
	formData.append("fileName", fileName);
	formData.append("folder", folder);

	const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
	const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

	if (!supabaseUrl || !anonKey) {
		throw new Error(
			"Faltan las variables de configuracion de Supabase en el frontend.",
		);
	}

	const response = await fetch(`${supabaseUrl}/functions/v1/upload-image`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${anonKey}`,
		},
		body: formData,
	});

	let result: { url?: string; error?: string } = {};
	const text = await response.text();
	try {
		result = JSON.parse(text);
	} catch {
		throw new Error(
			`El servidor respondio con codigo ${response.status}: ${text.slice(0, 200)}`,
		);
	}

	if (!response.ok || result.error) {
		throw new Error(result.error || `Error del servidor (${response.status})`);
	}

	if (!result.url) {
		throw new Error(
			"La subida se completo pero no se recibio la URL de la imagen.",
		);
	}

	return result.url;
}

async function uploadDirectToStorage(
	blob: Blob,
	fileName: string,
	folder: string,
): Promise<string> {
	const fullPath = `${folder}/${fileName}`;

	const { data: uploadData, error: uploadError } = await supabase.storage
		.from("images")
		.upload(fullPath, blob, {
			contentType: blob.type || "image/jpeg",
			upsert: true,
		});

	if (uploadError) {
		const name = (uploadError as any)?.name || "";
		const msg = (uploadError as any)?.message || JSON.stringify(uploadError);
		throw new Error(`[DirectUpload] ${name}: ${msg}`);
	}

	const { data: urlData } = supabase.storage
		.from("images")
		.getPublicUrl(uploadData?.path || fullPath);
	if (!urlData?.publicUrl) {
		throw new Error(
			"[DirectUpload] No se pudo obtener la URL publica de la imagen.",
		);
	}
	return urlData.publicUrl;
}

/**
 * Sube un blob ya procesado. Intenta la Edge Function y cae al storage directo.
 * `onProgress` recibe valores 0-100 aproximados. `ext` solo cambia para
 * archivos subidos tal cual (p. ej. GIF animados de publicidad).
 */
export async function uploadBlob(
	blob: Blob,
	folder: string,
	onProgress?: (p: number) => void,
	ext = "jpg",
): Promise<string> {
	onProgress?.(50);
	const timestamp = Date.now();
	const random = Math.random().toString(36).substring(2, 8);
	const fileName = `${timestamp}-${random}.${ext}`;

	let imageUrl: string;
	try {
		imageUrl = await uploadViaEdgeFunction(blob, fileName, folder);
	} catch (edgeErr) {
		onProgress?.(60);
		try {
			imageUrl = await uploadDirectToStorage(blob, fileName, folder);
		} catch (directErr) {
			const directMsg = directErr instanceof Error ? directErr.message : "";
			const edgeMsg = edgeErr instanceof Error ? edgeErr.message : "";
			throw new Error(
				`El servidor de subida no funciona. Error principal: ${classifyUploadError(edgeMsg)}. Error alternativo: ${classifyUploadError(directMsg)}`,
			);
		}
	}
	onProgress?.(100);
	return imageUrl;
}

/**
 * Redimensiona un archivo de imagen a JPEG optimizado (máx 1920×1440, calidad 0.9).
 */
export function resizeImageToBlob(file: File): Promise<Blob> {
	return new Promise<Blob>((resolve, reject) => {
		const img = new Image();
		const reader = new FileReader();
		reader.onload = (e) => {
			img.src = e.target?.result as string;
		};
		reader.onerror = () => reject(new Error("Error al leer el archivo."));
		img.onload = () => {
			const MAX_WIDTH = 1920;
			const MAX_HEIGHT = 1440;
			let finalW = img.width;
			let finalH = img.height;
			if (finalW > MAX_WIDTH) {
				finalH = Math.round(finalH * (MAX_WIDTH / finalW));
				finalW = MAX_WIDTH;
			}
			if (finalH > MAX_HEIGHT) {
				finalW = Math.round(finalW * (MAX_HEIGHT / finalH));
				finalH = MAX_HEIGHT;
			}
			const canvas = document.createElement("canvas");
			canvas.width = finalW;
			canvas.height = finalH;
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				reject(new Error("No se pudo procesar la imagen."));
				return;
			}
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(img, 0, 0, finalW, finalH);
			canvas.toBlob(
				(blob) => {
					if (blob) resolve(blob);
					else reject(new Error("No se pudo convertir la imagen."));
				},
				"image/jpeg",
				0.9,
			);
		};
		img.onerror = () => reject(new Error("No se pudo cargar la imagen."));
		reader.readAsDataURL(file);
	});
}

/**
 * Redimensiona y sube un archivo de imagen completo, devolviendo la URL pública.
 * Ideal para insertar imágenes en línea en el editor enriquecido.
 */
export async function uploadImageFile(
	file: File,
	folder = "articles",
	onProgress?: (p: number) => void,
): Promise<string> {
	onProgress?.(10);
	const blob = await resizeImageToBlob(file);
	onProgress?.(30);
	return uploadBlob(blob, folder, onProgress);
}
